import { cn } from "@/lib/utils/cn";
import { formatPLN } from "@/lib/utils/money";

type Tone = "money" | "muted" | "danger" | "ink" | "navy" | "white";

const TONE: Record<Tone, string> = {
  money: "text-forest",
  muted: "text-slate",
  danger: "text-[#B91C1C]",
  ink: "text-navy",
  navy: "text-navy",
  white: "text-white",
};

/**
 * Money rendering — JetBrains Mono via `font-num`.
 *
 * Default usage: `<MoneyText minor={4800} />` → "48,00 zł" (single-line, mono, navy).
 *
 * Hero usage: `<MoneyText minor={4800} variant="hero" tone="money" />` → 3-tier sizing
 * (54 / 30 / 24) per the dashboard handoff §5.3 — major in big, cents at 0.55× w/ opacity 0.85,
 * unit at 0.4× w/ opacity 0.7. The `size` prop sets the major number; cents+unit scale from it.
 */
export function MoneyText({
  minor,
  sign = false,
  tone,
  className,
  symbol = true,
  variant = "inline",
  size = 54,
}: {
  minor: number;
  sign?: boolean;
  tone?: Tone;
  symbol?: boolean;
  className?: string;
  variant?: "inline" | "hero";
  size?: number;
}) {
  const color = tone ? TONE[tone] : TONE.navy;

  if (variant === "hero") {
    const major = Math.floor(Math.abs(minor) / 100);
    const cents = String(Math.abs(minor) % 100).padStart(2, "0");
    const sgn = minor < 0 ? "−" : sign && minor > 0 ? "+" : "";
    return (
      <span
        className={cn(
          "font-display font-num inline-flex items-baseline tracking-tight leading-none",
          color,
          className,
        )}
        style={{ letterSpacing: "-0.04em" }}
      >
        <span style={{ fontSize: size, fontWeight: 800 }}>
          {sgn}
          {major}
        </span>
        <span style={{ fontSize: size * 0.55, fontWeight: 800, opacity: 0.85 }}>
          ,{cents}
        </span>
        {symbol && (
          <span
            className="font-sans"
            style={{
              fontSize: size * 0.4,
              fontWeight: 700,
              opacity: 0.7,
              marginLeft: 6,
              letterSpacing: 0,
            }}
          >
            zł
          </span>
        )}
      </span>
    );
  }

  return (
    <span className={cn("font-num", color, className)}>
      {formatPLN(minor, { sign, symbol })}
    </span>
  );
}
